import React, { useState, useRef, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  MdOutlineClose,
  MdOutlinePersonOutline,
  MdOutlineTv,
  MdOutlineWifi,
  MdOutlinePower,
} from "react-icons/md";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FiDownload, FiShare2, FiPhone, FiMessageSquare } from "react-icons/fi";
import { LuBedSingle, LuChefHat } from "react-icons/lu";
import { BiBath } from "react-icons/bi";
import { LiaSwimmingPoolSolid, LiaHotTubSolid } from "react-icons/lia";
import { PiCookingPotBold } from "react-icons/pi";
import { FaArrowLeft, FaArrowRight, FaRegBuilding } from "react-icons/fa";
import { IoStarSharp } from "react-icons/io5";
import { RiContactsLine } from "react-icons/ri";
import { GrCompliance } from "react-icons/gr";
import PropertyCarousel from "./PropertyCarousel";
import { FaMinus } from "react-icons/fa6";
import { GoArrowUpRight } from "react-icons/go";

import { VillaLuxo_data } from "../../Config";

import Header from "../Header/Header";
import ShortFooter from "../ShortFooter/ShortFooter";
import { useNavigate } from "react-router-dom";

const IconMap = {
  MdOutlinePersonOutline: <MdOutlinePersonOutline />,
  LuChefHat: <LuChefHat />,
  MdOutlineTv: <MdOutlineTv />,
  MdOutlineWifi: <MdOutlineWifi />,
  PiCookingPotBold: <PiCookingPotBold />,
  LiaSwimmingPoolSolid: <LiaSwimmingPoolSolid />,
  MdOutlinePower: <MdOutlinePower />,
  LiaHotTubSolid: <LiaHotTubSolid />,
};

/* ---------------- Desktop Full Screen Viewer  ---------------- */
const DesktopViewer = ({
  photos,
  selectedPhotoIndex,
  onClose,
  onNext,
  onPrev,
}) => {
  if (selectedPhotoIndex === null) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 cursor-pointer"
      onClick={onClose}
    >
      <div
        className="relative max-h-[90vh] max-w-full cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4  bg-white backdrop-blur-md rounded-full flex items-center justify-center text-black text-2xl z-[120] transition-all border border-white/20"
        >
          <MdOutlineClose size={20} />
        </button>

        <button
          onClick={onPrev}
          className="absolute p-1 left-4 top-1/2 -translate-y-1/2  bg-white/50 hover:bg-white backdrop-blur-md rounded-full flex items-center justify-center text-[#6D6148] text-3xl transition-all z-[110]"
        >
          <IoIosArrowBack size={20} />
        </button>

        <img
          src={photos[selectedPhotoIndex].src}
          alt="fullscreen"
          className="max-h-[90vh] max-w-full rounded-lg object-contain shadow-2xl block select-none"
        />

        <button
          onClick={onNext}
          className="absolute p-1 right-4 top-1/2 -translate-y-1/2 bg-white/50 hover:bg-white backdrop-blur-md rounded-full flex items-center justify-center text-[#6D6148] text-3xl transition-all z-[110]"
        >
          <IoIosArrowForward size={20} />
        </button>
      </div>
    </div>
  );
};

/* ---------------- Mobile Arrows ---------------- */
const MobilePrevArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 text-white w-5 h-5 rounded-full flex items-center justify-center"
  >
    <IoIosArrowBack />
  </button>
);

const MobileNextArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/70 text-white w-5 h-5 rounded-full flex items-center justify-center"
  >
    <IoIosArrowForward />
  </button>
);

/* ---------------- Main Component ---------------- */
const VillaLuxo = () => {
  const navigate = useNavigate();
  const photos = VillaLuxo_data.VillaImages;
  const amenitiesData = VillaLuxo_data.VillaAmenities;
  const safetyHygieneData = VillaLuxo_data.SafetyAndHygiene;
  const NearbyAttractions = VillaLuxo_data.NearbyAttractions;

  // carousel
  const [places, setPlaces] = useState(NearbyAttractions);
  const [blockedDates, setBlockedDates] = useState([]);

  const [isHandle_photos, setIsHandle_photos] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [showAllAmenities, setShowAllAmenities] = useState(false);

  const openPopup_photo = () => setIsHandle_photos(true);
  const closePopup_photos = () => setIsHandle_photos(false);

  const openDesktopViewer = (index) => setSelectedPhotoIndex(index);
  const closeDesktopViewer = () => setSelectedPhotoIndex(null);
  const nextPhoto = () =>
    setSelectedPhotoIndex((prev) => (prev + 1) % photos.length);
  const prevPhoto = () =>
    setSelectedPhotoIndex((prev) =>
      prev === 0 ? photos.length - 1 : prev - 1,
    );

  const mobileSettings = {
    dots: false,
    arrows: true,
    infinite: true,
    speed: 700,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3500,
    cssEase: "ease-in-out",
    prevArrow: <MobilePrevArrow />,
    nextArrow: <MobileNextArrow />,
  };

  const popupSettings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  const visibleAmenities = showAllAmenities
    ? amenitiesData
    : amenitiesData.slice(0, 4);

  const scrollRef = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);

  const scroll = (dir) => {
    if (isScrolling) return;
    setIsScrolling(true);

    const container = scrollRef.current;
    const card = container.querySelector(".nearby-card");
    const gap = 24;
    const scrollAmount = card.offsetWidth + gap;

    if (dir === "right") {
      container.scrollTo({
        left: container.scrollLeft + scrollAmount,
        behavior: "smooth",
      });

      setTimeout(() => {
        setPlaces((prev) => {
          const newState = [...prev];
          const first = newState.shift();
          return [...newState, first];
        });
        container.scrollLeft -= scrollAmount;
        setIsScrolling(false);
      }, 500);
    } else {
      setPlaces((prev) => {
        const newState = [...prev];
        const last = newState.pop();
        return [last, ...newState];
      });

      container.scrollLeft += scrollAmount;

      setTimeout(() => {
        container.scrollTo({
          left: container.scrollLeft - scrollAmount,
          behavior: "smooth",
        });
        setTimeout(() => setIsScrolling(false), 500);
      }, 10);
    }
  };

  const [showRules, setShowRules] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  // mobile slider
  const sliderRef = useRef(null);

  // calender
  const [open, setOpen] = useState(false);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  // Maximum future date allowed (90 days from today)
  const maxDate = new Date();
  maxDate.setDate(today.getDate() + 90);

  const format = (d) =>
    `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

  const [checkIn, setCheckIn] = useState(format(today));
  const [checkOut, setCheckOut] = useState(format(tomorrow));

  // Contact Advisor
  const [contactOpen, setContactOpen] = useState(false);

  const [isMinimized, setIsMinimized] = useState(false);
  const [villaData, setVillaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAvailable, setIsAvailable] = useState(true);
  useEffect(() => {
    fetch("https://sohamvillas.com/web-backend/villa/5")
      .then((res) => res.json())
      .then((data) => {
        setVillaData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching villa:", err);
        setLoading(false);
      });
  }, []);
  const mapSrc = villaData?.villaGmap?.data
    ? `data:image/jpeg;base64,${btoa(
        new Uint8Array(villaData.villaGmap.data).reduce(
          (acc, byte) => acc + String.fromCharCode(byte),
          "",
        ),
      )}`
    : "";

  useEffect(() => {
    fetch("https://sohamvillas.com/web-backend/villa-blocked-dates")
      .then((res) => res.json())
      .then((data) => {
        const list =
          data?.availability || // case 1: { availability: [...] }
          data ||
          []; // case 2: [ ... ]

        const targetVilla = list.find((v) => v.villaId === 5);

        setBlockedDates(targetVilla?.blockedDates || []);
      })
      .catch((err) => console.log("Error fetching blocked dates:", err));
  }, []);

  const convertToAPIFormat = (dateStr) => {
    const [day, month, year] = dateStr.split("/");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (!checkIn || !checkOut) return;

    const apiCheckIn = convertToAPIFormat(checkIn);
    const apiCheckOut = convertToAPIFormat(checkOut);

    fetch(
      `https://sohamvillas.com/web-backend/bookingAvailabilityController?startDate=${apiCheckIn}&endDate=${apiCheckOut}`,
    )
      .then((res) => res.json())
      .then((data) => {
        const villa = data.availability?.find((v) => v.villaId === 5);
        setIsAvailable(villa?.available === 1);
      })
      .catch((err) => {
        console.error("Error checking availability:", err);
        setIsAvailable(false); // Default to unavailable on error
      });
  }, [checkIn, checkOut]);
  // if (loading) return <p>Loading villa details...</p>;
  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex space-x-2">
          <span className="animate-bounce h-3 w-3 bg-gray-500 rounded-full"></span>
          <span className="animate-bounce h-3 w-3 bg-gray-400 rounded-full [animation-delay:-.15s]"></span>
          <span className="animate-bounce h-3 w-3 bg-gray-300 rounded-full [animation-delay:-.3s]"></span>
        </div>
      </div>
    );
  const isVillaSoldOut = () => {
    return !isAvailable;
  };
  return (
    <div className="bg-[#FAFAFA]">
      <Header />
      <br />
      <div className="bg-[#FAFAFA] min-h-screen pb-28 lg:pb-0">
        {/* ================= MOBILE IMG GRID ================= */}
        <div className="pt-16 -mb-8 mx-[5%] lg:hidden relative">
          <Slider {...mobileSettings}>
            {photos.map((photo, index) => (
              <div key={index} className="relative">
                <img
                  src={photo.src}
                  alt={`Img ${index + 1}`}
                  className="rounded-xl cursor-pointer min-h-60 w-full aspect-[5/3] object-cover object-center"
                  onClick={openPopup_photo}
                />
              </div>
            ))}
          </Slider>

          {isHandle_photos && (
            <>
              <div
                className="fixed inset-0 bg-black/60 z-[999] cursor-pointer"
                onClick={closePopup_photos}
              ></div>

              <div className="fixed inset-0 z-[1000] flex items-center justify-center pointer-events-none">
                <div
                  className="rounded-xl relative w-[95%] pointer-events-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span
                    className="absolute bg-black/20 hover:bg-black/50 text-white rounded-full p-0.5 top-1 right-1 cursor-pointer z-10"
                    onClick={closePopup_photos}
                  >
                    <MdOutlineClose />
                  </span>

                  <button
                    onClick={() => sliderRef.current?.slickPrev()}
                    className="absolute left-1 top-1/2 -translate-y-1/2 z-20 bg-black/70 text-white w-5 h-5 rounded-full flex items-center justify-center"
                  >
                    <IoIosArrowBack />
                  </button>

                  <button
                    onClick={() => sliderRef.current?.slickNext()}
                    className="absolute right-1 top-1/2 -translate-y-1/2 z-20 bg-black/70 text-white w-5 h-5 rounded-full flex items-center justify-center"
                  >
                    <IoIosArrowForward />
                  </button>

                  <div className="">
                    <Slider ref={sliderRef} {...popupSettings}>
                      {photos.map((photo, index) => (
                        <div key={index}>
                          <img
                            src={photo.src}
                            alt={`Popup ${index + 1}`}
                            className="rounded-xl w-full"
                          />
                        </div>
                      ))}
                    </Slider>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* DESKTOP IMG GRID SECTION */}
        <div className="mt-28 mx-[4%] hidden lg:block bg-[#FAFAFA]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 ">
            <div className="overflow-hidden rounded-2xl min-h-[470px] 2xl:h-[40vh]">
              <img
                src={photos[0].src}
                alt="Img"
                className="w-full h-full object-cover cursor-pointer transition duration-500"
                onClick={() => openDesktopViewer(0)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {photos.slice(1, 4).map((photo, index) => (
                <div
                  key={index}
                  className="overflow-hidden rounded-2xl min-h-[228px] 2xl:h-[19.5vh]"
                >
                  <img
                    src={photo.src}
                    alt={`Img ${index + 2}`}
                    className="w-full h-full object-cover cursor-pointer transition duration-500"
                    onClick={() => openDesktopViewer(index + 1)}
                  />
                </div>
              ))}

              <div className="relative overflow-hidden rounded-2xl min-h-[228px] 2xl:h-[19.5vh]">
                <img
                  src={photos[4].src}
                  alt="More"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40"></div>
                <button
                  className="absolute inset-0 flex items-center justify-center text-white"
                  onClick={() => openDesktopViewer(4)}
                >
                  <div className="flex items-center gap-2">
                    <p className="text-3xl font-semibold">+</p>
                    <p className="font-medium text-left leading-4">
                      <span className="text-2xl">More</span>
                      <br />
                      <span className="text-2xl">Photos</span>
                    </p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* MAIN CONTENT + STICKY CARD */}
        {/* <section className="mx-[5%] xl:mx-[9%] mt-12 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 pb-20"> */}
        <section className="mx-[5%] xl:mx-[8%] mt-12 grid grid-cols-1 lg:grid-cols-[60%_40%] 2xl:grid-cols-[70%_30%] gap-10 pb-20">
          {/* LEFT CONTENT */}
          <div>
            <div className="flex items-start justify-between gap-4 mb-8">
              <div>
                <h1 className="w-[80%] md:w-auto text-xl md:text-2xl 2xl:text-4xl font-semibold">
                  {villaData?.villaName || "Villa Name Unavailable"}
                </h1>
                <a
                  href={villaData?.mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <p className="text-[#333333] hover:underline text-[14px] md:text-xl lg:text-[16px] 2xl:text-2xl leading-6 md:leading-8 lg:leading-7 2xl:leading-10">
                    View on Map
                  </p>
                </a>
              </div>
              <div className="flex items-center">
                <a
                  href={VillaLuxo_data.PropertyBrochures}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-[#877d6f] transition px-4 py-2"
                >
                  <FiDownload className="text-xl" />
                  <span className="hidden md:inline text-base">Brochure</span>
                </a>

                <hr className="bg-gray-300 h-5 w-[1px] mx-2" />

                <button
                  onClick={() => {
                    const message = `Check out this amazing villa: Villa Luxo located near .
                Click here: sohamvillas.com/book-villa`;
                    window.open(
                      `https://wa.me/?text=${encodeURIComponent(message)}`,
                      "_blank",
                    );
                  }}
                  className="hover:scale-110 transition p-2 text-xl rounded-full focus:outline-none focus:ring-2 focus:ring-[#877d6f] active:bg-gray-100"
                >
                  <FiShare2 />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
              {[
                {
                  icon: <LuBedSingle />,
                  text: `${villaData?.max_bedrooms} Bedrooms`,
                },
                {
                  icon: <BiBath />,
                  text: `${villaData?.max_bathrooms} Bathrooms`,
                },
                { icon: <LiaSwimmingPoolSolid />, text: "Swimming Pool" },
              ].map((feature, i) => (
                <div
                  key={i}
                  className="bg-gray-100 rounded-xl py-6 flex flex-col items-center justify-center gap-0 border border-transparent hover:border-gray-200 transition"
                >
                  <span className="text-4xl text-gray-600">{feature.icon}</span>
                  <p className="text-center mx-[2%] lg:mt-[12%] text-[12px] md:text-sm 2xl:text-lg">
                    {feature.text}
                  </p>
                </div>
              ))}
            </div>

            <h2 className="text-xl md:text-xl 2xl:text-3xl font-semibold">
              Villa Description
            </h2>
            <p className="text-[12px] md:text-sm lg:text-[16px] 2xl:text-lg leading-6 md:leading-7 2xl:leading-8 my-[2%]">
              {villaData?.villaContent || "Villa Discription Unavailable"}
            </p>

            {/* OFFERED AMENITIES */}
            <div className="mt-10">
              <h2 className="text-xl md:text-xl 2xl:text-3xl font-semibold mb-6">
                Offered Amenities
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-5 gap-x-12 mb-8">
                {visibleAmenities.map((amenity, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <span className="text-2xl text-black">
                      {IconMap[amenity.icon]}
                    </span>
                    <span className="text-[15px] md:text-lg text-black font-normal">
                      {amenity.text}
                    </span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setShowAllAmenities(!showAllAmenities)}
                className="px-10 py-1 border-[1px] border-[#333333] text-black hover:bg-transparent rounded-full text-[15px] md:text-lg transition-all duration-300"
              >
                {showAllAmenities
                  ? "Show Less Amenities"
                  : "Show All Amenities"}
              </button>
            </div>

            {/* SAFETY SECTION */}
            <div className="mt-10">
              <h2 className="text-xl md:text-xl 2xl:text-3xl font-semibold">
                Safety and Hygiene
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 mt-[2%]">
                {safetyHygieneData.map((item, index) => (
                  <div key={index} className="flex items-center my-[2%]">
                    <span className="text-xl text-black">
                      <GrCompliance />
                    </span>
                    <span className="text-[15px] ml-3 md:text-lg text-black font-normal">
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* MAP SECTION */}
            <div className="mt-10">
              <div className="overflow-hidden border border-gray-200">
                <a href={villaData?.mapLink} target="_blank" rel="noreferrer">
                  <img
                    src={mapSrc}
                    alt="Location Map"
                    className="w-full h-80 object-cover"
                  />
                </a>
              </div>
            </div>

            {/* carousel */}
            {/* <div className="mt-10 relative px-4">
              <h2 className="text-xl md:text-xl 2xl:text-3xl font-semibold mb-6">
                Nearby Attractions
              </h2>

              <button onClick={() => scroll("left")} className="absolute left-3 bottom-12 -translate-y-1/2 bg-black/30 text-white shadow-md rounded-full p-3 z-10 hover:scale-105 transition">
                <FaArrowLeft size={15} />
              </button>

              <button onClick={() => scroll("right")} className="absolute right-3 lg:right-1 bottom-12 -translate-y-1/2 bg-black/30 text-white shadow-md rounded-full p-3 z-10 hover:scale-105 transition">
                <FaArrowRight size={15} />
              </button>

              <div ref={scrollRef} className="flex gap-3 no-scrollbar overflow-hidden">
                {places.map((item, i) => (
                  <a
                    key={`${item.name}-${i}`}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nearby-card min-w-[90%] md:min-w-[calc(47%-12px)] lg:min-w-[calc(47%-12px)] xl:min-w-[calc(48%-12px)] rounded-xl bg-white px-6 py-6 block shadow-md my-10 mx-2 first:ml-5 last:mr-5"
                  >
                    <h3 className="text-lg font-bold text-gray-800">{item.name}</h3>
                    
                    <div className="flex text-black mt-2 gap-1">
                      {[...Array(item.starCount || 5)].map((_, idx) => (
                        <IoStarSharp key={idx} size={14} />
                      ))}
                    </div>
                  </a>
                ))}
              </div>
            </div> */}
            <div className="mt-10 relative px-4">
              <h2 className="text-xl md:text-xl 2xl:text-3xl font-semibold mb-6">
                Nearby Attractions
              </h2>

              <button
                onClick={() => scroll("left")}
                className="absolute left-3 bottom-12 -translate-y-1/2 bg-black/30 text-white shadow-md rounded-full p-3 z-10 hover:scale-105 transition"
              >
                <FaArrowLeft size={15} />
              </button>

              <button
                onClick={() => scroll("right")}
                className="absolute right-3 lg:right-1 bottom-12 -translate-y-1/2 bg-black/30 text-white shadow-md rounded-full p-3 z-10 hover:scale-105 transition"
              >
                <FaArrowRight size={15} />
              </button>

              <div
                ref={scrollRef}
                className="flex gap-3 no-scrollbar overflow-hidden"
              >
                {places.map((item, i) => (
                  <a
                    key={`${item.name}-${i}`}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nearby-card min-w-[90%] md:min-w-[calc(47%-12px)] lg:min-w-[calc(47%-12px)] xl:min-w-[calc(48%-12px)] rounded-xl bg-white px-6 py-6 block shadow-md my-10 mx-2 first:ml-5 last:mr-5"
                  >
                    <h3 className="text-lg font-bold text-gray-800">
                      {item.name}
                    </h3>

                    <div className="flex text-black mt-2 gap-1">
                      {[...Array(item.starCount || 5)].map((_, idx) => (
                        <IoStarSharp key={idx} size={14} />
                      ))}
                    </div>
                  </a>
                ))}
              </div>
            </div>

            <a
              href={villaData?.mapLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="mb-10  px-10 py-1 border-[1px] border-[#333333] bg-[#333333] text-white hover:bg-transparent hover:text-[#333333] rounded-full text-[15px] md:text-lg transition-all duration-300">
                Show On Map
              </button>
            </a>

            {/* HOUSE RULES */}
            <div className="space-y-10">
              <div>
                <h2 className="text-xl md:text-xl 2xl:text-3xl font-semibold mb-6">
                  House Rules
                </h2>

                <ul className="text-[12px] md:text-sm lg:text-[16px] 2xl:text-lg leading-6 md:leading-7 2xl:leading-8 my-[2%] space-y-4">
                  <li>
                    • Check-in is available from 1:00 pm to 2:00 pm on the day
                    of arrival, while check-out is at 10:00 am on the day of
                    departure. Requests for early check-in or late check-out
                    will be accommodated based on availability and require
                    management approval.
                  </li>

                  <li>
                    • Guests must settle the full payment for their stay at
                    check-in, either in cash or via bank transfer.
                  </li>

                  {showRules && (
                    <>
                      <li>
                        • A security deposit of INR 20,000 is required at the
                        time of booking. This amount will be refunded within 7
                        business days following check-out.
                      </li>
                      <li>
                        • All guests are required to present valid national
                        photographic identification (such as a Passport, OCI
                        Card, Voter ID, or Driving License) along with a contact
                        number during check-in. Copies of these documents will
                        be made.
                      </li>
                      <li>
                        • Guests are responsible for their personal belongings
                        and valuables. Please ensure they are stored securely,
                        as SOHAM VILLAS and its staff cannot be held liable for
                        any loss or theft during your stay.
                      </li>
                      <li>
                        • As per Goa Police guidelines, loud music is not
                        permitted outdoors after 10:00 pm and indoors after
                        11:00 pm.
                      </li>
                      <li>
                        • Smoking is strictly prohibited inside the villa.
                        Guests may smoke in designated outdoor areas, including
                        the balcony, terrace, garden, or poolside. Please use
                        ashtrays and dispose of cigarette butts responsibly.
                      </li>
                      <li>
                        • Engaging in any commercial activities, such as
                        business or manufacturing, on the property is not
                        allowed. The villa is intended solely for residential
                        use.
                      </li>
                      <li>
                        • Any damage to the property, including gardens,
                        furnishings, or equipment, will be charged to the guest.
                        All waste must be disposed of in the designated bins.
                      </li>
                      <li>
                        • Children must be supervised at all times, especially
                        near the swimming pool. Guests under the influence of
                        alcohol are not permitted to enter the pool. Soham
                        Villas will not be responsible for any injuries or
                        accidents.
                      </li>
                      <li>
                        • Visitors who are not registered guests of Soham Villas
                        are not permitted to stay overnight.
                      </li>
                      <li>
                        • No animals of any kind are allowed on the property.
                      </li>
                      <li>
                        • The use, possession, or sale of narcotics is strictly
                        forbidden under Goa's anti-narcotics laws. Any breach of
                        this policy will result in immediate eviction from the
                        property and potential legal action.{" "}
                      </li>
                      <li>
                        • Our staff will not accept rude or inappropriate
                        behaviour from guests. Please refrain from disrupting
                        the daily housekeeping or routines of our team members.
                      </li>
                      <li>
                        • Guests will receive one key upon check-in, which must
                        be returned at check-out. Any loss or damage to the key
                        will result in a charge of INR 4,500. Personal locks are
                        not allowed on the property.
                      </li>
                      <li>
                        • Fires are not allowed anywhere on the villa premises.
                        Drying or airing clothing in visible outdoor areas is
                        prohibited.
                      </li>
                      <li>
                        • Hazardous items such as gas cylinders, firearms, or
                        flammable fuels are not permitted on the property.
                      </li>
                      <li>• Stag bookings are not allowed at Soham Villas. </li>
                      <li>
                        • For any additional items or requests, our staff is
                        here to assist you.
                      </li>
                    </>
                  )}
                </ul>

                <button
                  onClick={() => setShowRules(!showRules)}
                  className="mt-10 px-10 py-1 border-[1px] border-[#333333] text-[#333333] hover:bg-[#333333] hover:text-white rounded-full text-[15px] md:text-lg transition-all duration-300"
                >
                  {showRules ? "Read Less" : "Read More"}
                </button>
              </div>

              {/* ================= Cancellation Policy ================= */}
              <div>
                <h2 className="text-xl md:text-xl 2xl:text-3xl font-semibold ">
                  Cancellation Policy
                </h2>

                <ul className="text-[12px] md:text-sm lg:text-[16px] 2xl:text-lg leading-6 md:leading-7 2xl:leading-8 my-[2%] space-y-4">
                  <li>
                    • 100% refund on the total booking amount will be given if
                    the booking is cancelled 45 days before the check in date.
                  </li>

                  <li>
                    • 75% refund on the total booking amount will be given if
                    the booking is cancelled 30-44 days before the check in
                    date.
                  </li>

                  {showCancel && (
                    <>
                      <li>
                        • 50% refund on the total booking amount will be given
                        if booking is cancelled 29-15 days before the check in
                        date.
                      </li>
                      <li>
                        • No refund will be given if booking is cancelled less
                        than 15 days before the check in date.
                      </li>
                    </>
                  )}
                </ul>

                <button
                  onClick={() => setShowCancel(!showCancel)}
                  className="mt-10 px-10 py-1 border-[1px] border-[#333333] text-[#333333] hover:bg-[#333333] hover:text-white rounded-full text-[15px] md:text-lg transition-all duration-300"
                >
                  {showCancel ? "Read Less" : "Read More"}
                </button>
              </div>
            </div>
          </div>

          {/* DESKTOP STICKY CARD */}
          <>
            <div className="relative hidden lg:block">
              <div className="lg:sticky lg:top-24 2xl:top-[7vh] lg:h-fit space-y-5 w-full">
                <div className="bg-white rounded-2xl shadow-custom border p-3 space-y-5 m-3">
                  <button
                    onClick={() => setOpen(true)}
                    className="w-full border rounded-lg px-4 py-3 text-center text-sm 2xl:text-xl text-[#877D6F] transition"
                  >
                    CHECK - IN & CHECK - OUT
                    <br />
                    <span className="text-black text-lg">
                      {checkIn} - {checkOut}
                    </span>
                  </button>

                  <div>
                    <p className="text-2xl">
                      {" "}
                      {villaData?.price
                        ? `₹${villaData.price.toLocaleString()}  PER NIGHT`
                        : `₹${villaData.default_price.toLocaleString()}  PER NIGHT`}{" "}
                    </p>
                    <p className="text-gray-800 text-[10px] md:text-sm 2xl:text-lg leading-4">
                      (*Exclusive of taxes)
                    </p>

                    <hr className="mb-[3%] lg:my-[2%]" />

                    <p className="text-gray-800 text-[12px] md:text-sm 2xl:text-lg leading-4">
                      Taxes: 18%
                    </p>
                  </div>

                  <div className="flex items-center justify-center">
                    {isVillaSoldOut() ? (
                      <button
                        className="w-full mb-2 py-1 bg-[#656970] text-white rounded-full text-[15px] md:text-md cursor-default"
                        disabled
                      >
                        SOLD OUT
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          navigate("/checkout", {
                            state: {
                              check_in: checkIn,
                              check_out: checkOut,
                              villaDetails: {
                                id: villaData?.villaId,
                                villaName: villaData?.villaName,
                                price: villaData?.price,
                              },
                            },
                          });
                        }}
                        className="w-full mb-2 py-1 border-[1px] border-[#333333] bg-[#333333] text-white hover:bg-transparent hover:text-[#333333] rounded-full text-[15px] md:text-md transition-all duration-300"
                      >
                        Reserve Now
                      </button>
                    )}
                  </div>

                  <div className="flex justify-between text-xs font-semibold text-gray-600 pt-2 border-t">
                    <a href="/contact-us">
                      <button className="flex items-center text-[12px] md:text-sm 2xl:text-lg leading-4 gap-1">
                        <FaRegBuilding /> Property Inquiry
                      </button>
                    </a>

                    <div className="relative">
                      <button
                        onClick={() => setContactOpen(!contactOpen)}
                        className="flex items-center gap-1 text-[12px] md:text-sm 2xl:text-lg leading-4"
                      >
                        <RiContactsLine />
                        Contact Advisor
                      </button>

                      {contactOpen && (
                        <>
                          {/* Click outside layer */}
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setContactOpen(false)}
                          />

                          {/* Dropdown */}
                          <div className="absolute right-0 mt-2 w-32 bg-white border rounded-lg shadow-lg z-50">
                            <a href="tel:+918866262368">
                              <button className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-100">
                                <FiPhone /> Call
                              </button>
                            </a>

                            <a
                              href="https://wa.me/+918866262368"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <button className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-100">
                                <FiMessageSquare /> Message
                              </button>
                            </a>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl shadow-lg border overflow-hidden mb-16 m-3">
                  <div className="relative aspect-video">
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src={villaData?.youtubeLink}
                      title="Villa Luxo"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>

            {open && (
              <DateRangeModal
                onClose={() => setOpen(false)}
                setCheckIn={setCheckIn}
                setCheckOut={setCheckOut}
                blockedDates={blockedDates}
              />
            )}
          </>
        </section>

        {/* MOBILE STICKY CARD SECTION */}
        <div className="fixed bottom-1 left-3 z-40 lg:hidden">
          {!isMinimized && (
            <div className="max-w-[330px] rounded-2xl bg-white border-t shadow-[0_-8px_30px_rgba(0,0,0,0.08)] lg:hidden">
              <div className="bg-white rounded-2xl shadow-xl border p-3 space-y-0">
                <div className="flex justify-end">
                  <button
                    onClick={() => setIsMinimized(true)}
                    className="text-gray-500 hover:text-black text-sm p-0.5 mb-1.5"
                  >
                    <FaMinus />
                  </button>
                </div>

                <button
                  onClick={() => setOpen(true)}
                  className="w-full border rounded-lg px-3 py-2 text-center text-sm 2xl:text-xl text-[#877D6F] transition"
                >
                  CHECK - IN & CHECK - OUT
                  <br />
                  <span className="text-black text-sm">
                    {checkIn} - {checkOut}
                  </span>
                </button>

                <div>
                  <p className="text-lg md:text-2xl">
                    {" "}
                    {villaData?.price
                      ? `₹${villaData.price.toLocaleString()}  PER NIGHT`
                      : `₹${villaData.default_price.toLocaleString()}  PER NIGHT`}
                  </p>
                  <p className="text-gray-800 text-[10px] md:text-xs leading-4">
                    (*Exclusive of taxes)
                  </p>

                  <hr className="my-2" />

                  <p className="text-gray-800 text-[12px] md:text-sm leading-4">
                    Taxes: 18%
                  </p>
                </div>

                <div className="flex items-center justify-center">
                  {isVillaSoldOut() ? (
                    <button
                      className="w-full mb-2 py-1 bg-[#656970] text-white rounded-full text-[15px] md:text-md cursor-default"
                      disabled
                    >
                      SOLD OUT
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        navigate("/checkout", {
                          state: {
                            check_in: checkIn,
                            check_out: checkOut,
                            villaDetails: {
                              id: villaData?.villaId,
                              villaName: villaData?.villaName,
                              price: villaData?.price,
                            },
                          },
                        });
                      }}
                      className="w-full mb-2 py-1 border-[1px] border-[#333333] bg-[#333333] text-white hover:bg-transparent hover:text-[#333333] rounded-full text-[15px] md:text-md transition-all duration-300"
                    >
                      Reserve Now
                    </button>
                  )}
                </div>

                <div className="flex justify-between gap-2 text-xs font-semibold text-gray-600 pt-2 border-t">
                  <a href="/contact-us">
                    <button className="flex items-center text-[12px] md:text-sm gap-1">
                      <FaRegBuilding /> Property Inquiry
                    </button>
                  </a>

                  <div className="relative">
                    <button
                      onClick={() => setContactOpen(!contactOpen)}
                      className="flex items-center gap-1 text-[12px] md:text-sm"
                    >
                      <RiContactsLine />
                      Contact Advisor
                    </button>

                    {contactOpen && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setContactOpen(false)}
                        />
                        <div className="absolute right-0 bottom-full mb-2 w-28 bg-white border rounded-lg shadow-lg z-50">
                          <a href="tel:+918866262368">
                            <button className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-100">
                              <FiPhone /> Call
                            </button>
                          </a>
                          <a
                            href="https://wa.me/+918866262368"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <button className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-gray-100">
                              <FiMessageSquare /> Message
                            </button>
                          </a>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {isMinimized && (
            <button
              onClick={() => setIsMinimized(false)}
              className="w-10 h-10 rounded-full bg-white border-[1.5px] border-[#877D6F] shadow-custom flex items-center justify-center text-[#877D6F] text-lg animate-bounce"
            >
              <GoArrowUpRight />
            </button>
          )}
        </div>

        {/* mobile vedio */}
        <div className="bg-white rounded-2xl shadow-lg border overflow-hidden lg:hidden md:block -mt-5 mb-10 m-5">
          <div className="relative aspect-video">
            <iframe
              className="absolute inset-0 w-full h-full"
              src={villaData?.youtubeLink}
              title="Villa Luxo"
              allowFullScreen
            ></iframe>
          </div>
        </div>

        <PropertyCarousel />

        {/* ================= FULLSCREEN VIEWER ================= */}
        {selectedPhotoIndex !== null && (
          <DesktopViewer
            photos={photos}
            selectedPhotoIndex={selectedPhotoIndex}
            onClose={closeDesktopViewer}
            onNext={nextPhoto}
            onPrev={prevPhoto}
          />
        )}
      </div>
      <ShortFooter />
    </div>
  );
};

/* CALENDAR  */
/* CALENDAR  */
function DateRangeModal({
  onClose,
  setCheckIn,
  setCheckOut,
  blockedDates = [],
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  /* ========= ADD 90-DAY LIMIT ========= */
  const maxDate = new Date();
  maxDate.setDate(today.getDate() + 90);
  maxDate.setHours(0, 0, 0, 0);

  const isDateDisabled = (date) => date > maxDate;
  /* ==================================== */

  const [currentDate, setCurrentDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // CLEAN BLOCKED DATES → REMOVE CHECKOUT DAYS
  function cleanBlockedDates(list) {
    const cleaned = [];

    list.forEach((dateStr) => {
      const d = new Date(dateStr);

      // Find if next date also exists → means this one is NOT checkout
      const next = new Date(d);
      next.setDate(d.getDate() + 1);

      const hasNextDay = list.includes(next.toISOString().slice(0, 10));

      if (hasNextDay) cleaned.push(dateStr); // keep only stay nights
    });

    return cleaned;
  }

  const cleanedBlocked = cleanBlockedDates(blockedDates);
  const blocked = cleanedBlocked.map((d) => new Date(d));

  // const blocked = blockedDates.map((d) => new Date(d));

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString();
  const isRangeBlocked = (start, end) => {
    if (!start || !end) return false;

    let d = new Date(start);
    while (d <= end) {
      if (isBlocked(d)) return true;
      d.setDate(d.getDate() + 1);
    }
    return false;
  };
  const handleDateClick = (day) => {
    const selected = new Date(year, month, day);

    // If resetting selection
    if ((startDate && endDate) || (startDate && selected < startDate)) {
      setStartDate(selected);
      setEndDate(null);
      return;
    }

    // Selecting start
    if (!startDate) {
      setStartDate(selected);
      return;
    }

    // Attempting to select end
    if (startDate && !endDate && selected > startDate) {
      // Check if range contains blocked dates
      if (isRangeBlocked(startDate, selected)) {
        alert(
          "You cannot select this range because it contains blocked dates.",
        );
        return; // ❌ Do not allow
      }

      setEndDate(selected); // ✔ Allowed
    }
  };

  const applyDates = () => {
    const format = (d) =>
      `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

    if (startDate && endDate) {
      setCheckIn(format(startDate));
      setCheckOut(format(endDate));
      onClose();
    }
  };

  const isBlocked = (date) =>
    blocked.some((b) => b.toDateString() === date.toDateString());

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white shadow-xl relative w-full max-w-[420px] rounded-2xl p-6"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-black"
        >
          ✕
        </button>

        <h3 className="text-lg font-bold mb-4 text-[#877d6f]">CALENDAR</h3>

        {/* Check In / Out Display */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="border rounded-lg py-2 px-3 text-center">
            <p className="text-[10px] text-gray-400 uppercase font-medium">
              Check-in
            </p>
            <p className="text-sm text-[#333333]">
              {startDate
                ? `${startDate.getDate()} ${monthNames[startDate.getMonth()].slice(0, 3)} ${startDate.getFullYear()}`
                : "-- / -- / --"}
            </p>
          </div>

          <div className="border rounded-lg py-2 px-3 text-center">
            <p className="text-[10px] text-gray-400 uppercase font-medium">
              Check-out
            </p>
            <p className="text-sm text-[#333333]">
              {endDate
                ? `${endDate.getDate()} ${monthNames[endDate.getMonth()].slice(0, 3)} ${endDate.getFullYear()}`
                : "-- / -- / --"}
            </p>
          </div>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
            className="p-2 hover:bg-gray-100 rounded-full transition"
          >
            <IoIosArrowBack />
          </button>

          <div className="font-bold text-[#877d6f]">
            {monthNames[month]} {year}
          </div>

          <button
            /* ⛔ DISABLE NEXT MONTH if it goes beyond maxDate */
            disabled={new Date(year, month + 1, 1) > maxDate}
            onClick={() => {
              if (new Date(year, month + 1, 1) <= maxDate) {
                setCurrentDate(new Date(year, month + 1, 1));
              }
            }}
            className="p-2 hover:bg-gray-100 rounded-full transition disabled:opacity-30"
          >
            <IoIosArrowForward />
          </button>
        </div>

        {/* Day Labels */}
        <div className="grid grid-cols-7 text-center mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div
              key={d}
              className="text-[11px] text-gray-400 font-medium uppercase"
            >
              {d}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 text-center">
          {[...Array(firstDay)].map((_, i) => (
            <div key={`empty-${i}`} />
          ))}

          {[...Array(daysInMonth)].map((_, i) => {
            const day = i + 1;
            const current = new Date(year, month, day);

            const isPast = current < today;
            const blockedDay = isBlocked(current);
            const tooFar = isDateDisabled(current);

            const isStart = isSameDay(current, startDate);
            const isEnd = isSameDay(current, endDate);
            const isInRange =
              startDate && endDate && current > startDate && current < endDate;

            return (
              <div key={day} className="relative py-1">
                {isInRange && (
                  <div className="absolute inset-y-1 left-0 right-0 bg-[#E3DED7]" />
                )}
                {isStart && endDate && (
                  <div className="absolute inset-y-1 left-1/2 right-0 bg-[#E3DED7]" />
                )}
                {isEnd && startDate && (
                  <div className="absolute inset-y-1 left-0 right-1/2 bg-[#E3DED7]" />
                )}

                <button
                  disabled={isPast || blockedDay || tooFar}
                  onClick={() => !blockedDay && !tooFar && handleDateClick(day)}
                  className={`
                    relative z-10 w-9 h-9 lg:w-10 lg:h-10 text-xs lg:text-sm rounded-full transition-all

                    ${isStart || isEnd ? "bg-[#B8B1A5] text-white shadow-md" : ""}
                    ${isInRange ? "text-[#333333]" : ""}
                    ${isPast ? "text-gray-400 cursor-not-allowed" : ""}
                    ${blockedDay ? "line-through text-gray-400 cursor-not-allowed" : ""}
                    ${tooFar ? "opacity-40 cursor-not-allowed" : ""}
                  `}
                >
                  {day}
                </button>
              </div>
            );
          })}
        </div>

        <button
          onClick={applyDates}
          disabled={!startDate || !endDate}
          className="mt-6 w-full py-3 bg-[#333333] text-white rounded-full font-bold text-sm border border-[#333333] disabled:opacity-30 transition-all"
        >
          Apply Dates
        </button>
      </div>
    </div>
  );
}

export default VillaLuxo;
